// In-game HUD overlay. DOM-based (styled inline) to match the rest of the
// game's UI convention. Layout matches `references/ingame-mid-game.png`:
//   top-left:    Stamina (orange) + Break-free (grey/yellow) bars
//   top-center:  "Round N" label
//   top-right:   per-team blurred card with player-color head avatars,
//                team label, and 3 crown slots
// Also owns a muted FPS counter (bottom-left when enabled) and the
// debug HP/KO overlay toggled via CONFIG.sim.showHealthBars.

import type { SimState } from '../sim/SimState';
import { CONFIG } from '../config';
import type { MatchState, TeamMember } from '../sim/MatchState';
import { TEAM_COLORS } from '../sim/MatchState';
import type { TeamId } from '../sim/SimState';
import { renderHeadIcon } from './HeadIcon';
import { t, onLocaleChange } from '../i18n';
import { isDevMode } from './devMode';

export interface HudOptions {
  match: MatchState;
}

// Background + border tokens shared by the top-right team card and the
// modal overlays (keep the styling consistent).
const PANEL_BG = 'rgba(24, 28, 36, 0.55)';
const PANEL_BORDER = '1px solid rgba(255, 255, 255, 0.08)';

const CROWN_SRC = '/ui/game-crown.webp';

export class Hud {
  private root: HTMLElement;
  private match: MatchState;

  // Top-left bars
  private staminaWrap: HTMLElement;
  private staminaFill: HTMLElement;
  private breakFreeWrap: HTMLElement;
  private breakFreeFill: HTMLElement;

  // Top-center round label
  private roundLabel: HTMLElement;

  // Top-right team panels (one row per team, in fixed order: blue, red)
  private teamRows = new Map<TeamId, HTMLElement>();
  private teamHeadEls = new Map<number, HTMLElement>(); // per character id
  private teamCrownEls = new Map<TeamId, HTMLImageElement[]>();
  private teamCrownWrapEls = new Map<TeamId, HTMLElement>();

  // Debug health panel (unchanged)
  private debugWrap: HTMLElement;
  private playerHpBar: HTMLElement;
  private playerKoBar: HTMLElement;
  private aiHpBar: HTMLElement;
  private aiKoBar: HTMLElement;

  private detachLocaleListener: (() => void) | null = null;
  private fpsEl: HTMLElement;
  // Always-on local-pelvis XYZ readout. Used during map design to pick
  // spawn coords on a new model — walk to a candidate spot, read the
  // values, hand them to the map.json `spawns[]` entry.
  private posEl: HTMLElement;
  // FPS measurement is driven off requestAnimationFrame, which fires once
  // per browser paint — i.e. once per actually-presented frame. This is
  // independent of the GameLoop's tick cadence (which can be `setTimeout(0)`
  // and run far faster than the display can present), so the readout
  // reflects what the user sees, not how often the JS loop runs.
  private fpsRafTimes: number[] = [];
  private fpsRafHandle = 0;
  private fpsLastFlushMs = 0;

  constructor(opts: HudOptions) {
    this.match = opts.match;
    this.root = document.getElementById('hud')!;
    this.root.innerHTML = STYLE_BLOCK + this.markup();

    this.staminaWrap = this.root.querySelector('#stamina-wrap')!;
    this.staminaFill = this.root.querySelector('#stamina-fill')!;
    this.breakFreeWrap = this.root.querySelector('#breakfree-wrap')!;
    this.breakFreeFill = this.root.querySelector('#breakfree-fill')!;
    this.roundLabel = this.root.querySelector('#round-label')!;
    this.fpsEl = this.root.querySelector('#fps')!;
    this.posEl = this.root.querySelector('#pos')!;
    // Hide debug FPS + pelvis-XYZ readouts outside of dev sessions. They
    // sit at the bottom-left and are intended for engine debugging — not
    // user-facing.
    if (!isDevMode()) {
      this.fpsEl.style.display = 'none';
      this.posEl.style.display = 'none';
    }
    this.debugWrap = this.root.querySelector('#debug-health')!;
    this.playerHpBar = this.root.querySelector('#bar-player-hp')!;
    this.playerKoBar = this.root.querySelector('#bar-player-ko')!;
    this.aiHpBar = this.root.querySelector('#bar-ai-hp')!;
    this.aiKoBar = this.root.querySelector('#bar-ai-ko')!;

    this.teamRows.set('blue', this.root.querySelector('#team-row-blue')!);
    this.teamRows.set('red',  this.root.querySelector('#team-row-red')!);
    for (const m of this.match.members) {
      const el = this.root.querySelector(`#head-${m.id}`) as HTMLElement;
      if (el) this.teamHeadEls.set(m.id, el);
    }
    for (const team of ['blue', 'red'] as TeamId[]) {
      const wrap = this.root.querySelector(`#crowns-${team}`) as HTMLElement;
      this.teamCrownWrapEls.set(team, wrap);
      const crowns: HTMLImageElement[] = [];
      for (let i = 0; i < 3; i++) {
        const img = document.createElement('img');
        img.src = CROWN_SRC;
        img.className = 'crown off';
        img.alt = '';
        img.draggable = false;
        wrap.appendChild(img);
        crowns.push(img);
      }
      this.teamCrownEls.set(team, crowns);
    }

    // Kick off head-avatar renders for every roster member. Each render is
    // async (WebGPU init + GLB fetch for hatted NPCs), so the <img> src is
    // swapped in when the promise resolves — the placeholder color block
    // bridges the gap without flashing transparent.
    for (const m of this.match.members) this.fillHeadIcon(m);

    this.startFpsLoop();

    // Re-translate every static HUD label when the user picks a new
    // language from the in-match settings modal.
    this.detachLocaleListener = onLocaleChange(() => this.refreshLocale());
  }

  /** Update every static translatable label in the HUD when the user
   *  picks a new language from the in-match settings modal. Live values
   *  (timers, round number) are re-formatted with the new locale. */
  private refreshLocale(): void {
    const staminaLbl = this.root.querySelector('.stamina-label') as HTMLElement | null;
    if (staminaLbl) staminaLbl.textContent = t('hud.stamina');
    const breakLbl = this.root.querySelector('.breakfree-label') as HTMLElement | null;
    if (breakLbl) breakLbl.textContent = t('hud.breakFree');
    this.roundLabel.textContent = t('hud.round', { round: this.match.round });
    const blueName = this.root.querySelector('#team-row-blue .team-name') as HTMLElement | null;
    if (blueName) blueName.textContent = t('hud.blue');
    const redName = this.root.querySelector('#team-row-red .team-name') as HTMLElement | null;
    if (redName) redName.textContent = t('hud.red');
    const youName = this.root.querySelector('.name.you') as HTMLElement | null;
    if (youName) youName.textContent = t('hud.you');
    const enemyName = this.root.querySelector('.name.ai') as HTMLElement | null;
    if (enemyName) enemyName.textContent = t('hud.enemy');
  }

  private markup(): string {
    const blue = this.match.membersOfTeam('blue');
    const red  = this.match.membersOfTeam('red');
    const headMarkup = (m: TeamMember): string => `
      <span class="head" id="head-${m.id}" style="background:${m.color};"></span>
    `;
    return `
      <div id="stamina-wrap" class="bar-wrap">
        <div class="bar-label stamina-label">${t('hud.stamina')}</div>
        <div class="bar-track"><div id="stamina-fill" class="bar-fill stamina-fill"></div></div>
      </div>
      <div id="breakfree-wrap" class="bar-wrap">
        <div class="bar-label breakfree-label">${t('hud.breakFree')}</div>
        <div class="bar-track"><div id="breakfree-fill" class="bar-fill breakfree-fill"></div></div>
      </div>

      <div id="round-label" class="round-label">${t('hud.round', { round: 1 })}</div>

      <div id="team-panel">
        <div id="team-row-blue" class="team-row">
          <div class="heads">${blue.map(headMarkup).join('')}</div>
          <div class="team-name" style="color:${TEAM_COLORS.blue.primary};">${t('hud.blue')}</div>
          <div class="crowns" id="crowns-blue"></div>
        </div>
        <div id="team-row-red" class="team-row">
          <div class="heads">${red.map(headMarkup).join('')}</div>
          <div class="team-name" style="color:${TEAM_COLORS.red.primary};">${t('hud.red')}</div>
          <div class="crowns" id="crowns-red"></div>
        </div>
      </div>

      <div id="fps" class="fps">${t('hud.fpsPlaceholder')}</div>

      <div id="pos" class="pos">x: 0.00&nbsp;&nbsp;y: 0.00&nbsp;&nbsp;z: 0.00</div>

      <div class="debug-health" id="debug-health">
        <div class="player">
          <div class="name you">${t('hud.you')}</div>
          <div class="bar-label">${t('hud.hp')}</div>
          <div class="bar hp"><div id="bar-player-hp"></div></div>
          <div class="bar-label">${t('hud.ko')}</div>
          <div class="bar ko"><div id="bar-player-ko"></div></div>
        </div>
        <div class="player">
          <div class="name ai">${t('hud.enemy')}</div>
          <div class="bar-label">${t('hud.hp')}</div>
          <div class="bar hp"><div id="bar-ai-hp"></div></div>
          <div class="bar-label">${t('hud.ko')}</div>
          <div class="bar ko"><div id="bar-ai-ko"></div></div>
        </div>
      </div>
    `;
  }

  private async fillHeadIcon(m: TeamMember): Promise<void> {
    const el = this.teamHeadEls.get(m.id);
    if (!el) return;
    try {
      const canvas = await renderHeadIcon({ color: m.color, hatId: m.hatId, size: 96 });
      const url = canvas.toDataURL('image/png');
      el.style.backgroundImage = `url(${url})`;
      el.style.backgroundColor = 'transparent';
      el.style.backgroundSize = 'cover';
      el.style.backgroundPosition = 'center 22%';
    } catch (err) {
      console.warn('[Hud] head icon render failed for', m.id, err);
    }
  }

  /** Kept as a no-op so existing call sites (3 in main.ts) compile without
   *  edits. Frame counting is owned by the internal rAF loop now. */
  tickFps(_realDt: number): void { /* no-op */ }

  private startFpsLoop(): void {
    const tick = (now: number): void => {
      this.fpsRafTimes.push(now);
      // Trim to the last 1 second so the readout always reflects "roughly
      // the current second" of presented frames.
      const cutoff = now - 1000;
      while (this.fpsRafTimes.length > 0 && this.fpsRafTimes[0]! < cutoff) {
        this.fpsRafTimes.shift();
      }
      // Update the visible readout every ~200ms — frequent enough to feel
      // live, infrequent enough that users can read it.
      if (now - this.fpsLastFlushMs >= 200) {
        this.fpsLastFlushMs = now;
        if (this.fpsRafTimes.length >= 2) {
          const span = this.fpsRafTimes[this.fpsRafTimes.length - 1]! - this.fpsRafTimes[0]!;
          const fps = span > 0 ? (this.fpsRafTimes.length - 1) * 1000 / span : 0;
          this.fpsEl.textContent = t('hud.fps', { fps: Math.round(fps) });
        }
      }
      this.fpsRafHandle = requestAnimationFrame(tick);
    };
    this.fpsRafHandle = requestAnimationFrame(tick);
  }

  update(snap: SimState): void {
    const local = this.match.localPlayer();
    const localSnap = snap.characters.find((c) => c.id === local.id);
    const localDead = !!localSnap && (localSnap.wasted || localSnap.ringedOut);

    // --- Top-left bars ---
    // Hide stamina + break-free entirely once the local player is dead —
    // we're spectating a teammate, those bars don't belong to us anymore.
    const barsHidden = localDead;
    this.staminaWrap.classList.toggle('hidden', barsHidden);
    this.breakFreeWrap.classList.toggle('hidden', barsHidden);

    if (localSnap && !barsHidden) {
      this.staminaFill.style.width = `${clamp01(localSnap.stamina) * 100}%`;
      this.staminaWrap.classList.toggle('locked', localSnap.staminaLocked);
      const wriggleMax = CONFIG.character.grab.wriggleMax || 1;
      const wr = clamp01(localSnap.wriggle / wriggleMax);
      this.breakFreeFill.style.width = `${wr * 100}%`;
      this.breakFreeWrap.classList.toggle('active', localSnap.beingGrabbed);
    }

    // --- Top-center round label ---
    this.roundLabel.textContent = t('hud.round', { round: this.match.round });

    // --- Pelvis XYZ readout (spawn-finder) ---
    if (localSnap) {
      this.posEl.textContent =
        `x: ${localSnap.pelvisX.toFixed(2)}  ` +
        `y: ${localSnap.pelvisY.toFixed(2)}  ` +
        `z: ${localSnap.pelvisZ.toFixed(2)}`;
    }

    // --- Top-right team panel ---
    // Head fade when that character is out for the round.
    for (const m of this.match.members) {
      const el = this.teamHeadEls.get(m.id);
      if (!el) continue;
      const cs = snap.characters.find((c) => c.id === m.id);
      const dead = !!cs && (cs.wasted || cs.ringedOut);
      el.classList.toggle('dead', dead);
    }
    // Crown slot state (won vs un-won) — per-team.
    for (const team of ['blue', 'red'] as TeamId[]) {
      const won = this.match.crowns[team];
      const crowns = this.teamCrownEls.get(team);
      if (!crowns) continue;
      for (let i = 0; i < crowns.length; i++) {
        const isWon = i < won;
        crowns[i]!.classList.toggle('off', !isWon);
      }
    }

    // --- Debug overlay (unchanged, hidden unless config enables) ---
    const showBars = CONFIG.sim.showHealthBars;
    this.debugWrap.style.display = showBars ? 'flex' : 'none';
    if (showBars) {
      const hpMax = CONFIG.character.hpMax || 1;
      const koMax = CONFIG.character.koMax || 1;
      const player = snap.characters.find((c) => c.id === local.id);
      const ai = snap.characters.find((c) => c.id !== local.id);
      if (player) {
        this.playerHpBar.style.width = `${clamp01(player.hp / hpMax) * 100}%`;
        this.playerKoBar.style.width = `${clamp01(player.ko / koMax) * 100}%`;
      }
      if (ai) {
        this.aiHpBar.style.width = `${clamp01(ai.hp / hpMax) * 100}%`;
        this.aiKoBar.style.width = `${clamp01(ai.ko / koMax) * 100}%`;
      }
    }
  }

  dispose(): void {
    cancelAnimationFrame(this.fpsRafHandle);
    this.detachLocaleListener?.();
    this.detachLocaleListener = null;
    this.root.innerHTML = '';
  }
}

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

const STYLE_BLOCK = `
<style>
  @import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');
  #hud { font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif; color: #EEF4F6; }

  /* ---------- top-left bars ---------- */
  .bar-wrap {
    position: fixed; left: 20px; width: 360px;
    display: flex; align-items: center; gap: 12px;
    pointer-events: none;
    transition: opacity 180ms ease;
  }
  .bar-wrap.hidden { opacity: 0; }
  #stamina-wrap   { top: 20px; }
  #breakfree-wrap { top: 54px; }
  .bar-label {
    font-size: 20px; letter-spacing: 0.02em;
    -webkit-text-stroke: 4px #1a2230; paint-order: stroke fill;
    min-width: 96px; text-align: right;
  }
  .stamina-label {
    color: #FF7F1F;
  }
  .breakfree-label {
    color: #B0B0B0;
    transition: color 220ms ease;
  }
  #breakfree-wrap.active .breakfree-label { color: #FFD15A; }
  .bar-track {
    flex: 1; height: 14px;
    background: rgba(0,0,0,0.55);
    border-radius: 9px;
    box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), inset 0 0 0 2px rgba(0,0,0,0.35);
    overflow: hidden;
  }
  .bar-fill {
    height: 100%; width: 100%;
    transform-origin: left center;
    transition: width 80ms linear, background 220ms ease;
  }
  .stamina-fill {
    background: linear-gradient(90deg, #FFC864, #FF8A1E);
  }
  #stamina-wrap.locked .stamina-fill {
    background: linear-gradient(90deg, #a83030, #ff7a7a);
  }
  .breakfree-fill {
    background: #8e8e8e;
    opacity: 0.55;
    transition: background 220ms ease, opacity 220ms ease, width 80ms linear;
  }
  #breakfree-wrap.active .breakfree-fill {
    background: linear-gradient(90deg, #FFD15A, #FFA91A);
    opacity: 1;
  }

  /* ---------- top-center round ---------- */
  .round-label {
    position: fixed; top: 16px; left: 50%;
    transform: translateX(-50%);
    font-size: 44px; line-height: 1;
    color: #FFFFFF;
    -webkit-text-stroke: 6px #222833; paint-order: stroke fill;
    text-shadow: 0 4px 16px rgba(0,0,0,0.35);
    letter-spacing: 0.01em;
    pointer-events: none;
  }

  /* ---------- top-right team panel ---------- */
  #team-panel {
    position: fixed; top: 16px; right: 18px;
    display: flex; flex-direction: column; gap: 6px;
    pointer-events: none;
    padding: 8px 14px;
    background: ${PANEL_BG};
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: ${PANEL_BORDER};
    border-radius: 14px;
    box-shadow: 0 6px 20px rgba(0,0,0,0.25);
  }
  .team-row {
    display: flex; align-items: center; gap: 10px;
    padding: 4px 2px;
    min-width: 280px;
  }
  .team-row .heads {
    display: flex; gap: 4px;
  }
  .team-row .head {
    display: inline-block;
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 2px solid rgba(0,0,0,0.35);
    background-size: cover;
    background-position: center 22%;
    box-shadow: 0 1px 4px rgba(0,0,0,0.35);
    transition: filter 250ms ease, opacity 250ms ease;
  }
  .team-row .head.dead {
    filter: grayscale(1) brightness(0.55);
    opacity: 0.45;
  }
  .team-row .team-name {
    font-size: 28px; line-height: 1;
    -webkit-text-stroke: 4px #1b212a;
    paint-order: stroke fill;
    letter-spacing: 0.02em;
    margin-right: auto;
  }
  .team-row .crowns {
    display: flex; gap: 3px;
  }
  .crown {
    width: 28px; height: 28px;
    object-fit: contain;
    filter: none;
    transition: filter 260ms ease-out, transform 260ms cubic-bezier(.2,.9,.2,1);
  }
  .crown.off {
    filter: grayscale(1) brightness(0.45);
  }

  /* ---------- fps (kept for debugging) ---------- */
  .fps {
    position: fixed; bottom: 10px; left: 14px;
    font-variant-numeric: tabular-nums; font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    letter-spacing: 0.06em; color: #9fe8a8;
    background: rgba(0,0,0,0.38);
    padding: 4px 8px; border-radius: 6px;
    pointer-events: none;
  }

  /* ---------- pelvis XYZ readout (spawn-finder / map-design overlay) ---------- */
  .pos {
    position: fixed; bottom: 10px; left: 100px;
    font-variant-numeric: tabular-nums; font-size: 12px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    letter-spacing: 0.06em; color: #ffd56b;
    background: rgba(0,0,0,0.38);
    padding: 4px 8px; border-radius: 6px;
    pointer-events: none;
  }

  /* ---------- debug HP/KO overlay (unchanged behaviour) ---------- */
  .debug-health {
    position: fixed; top: 90px; left: 50%;
    transform: translateX(-50%);
    display: none; gap: 28px; align-items: flex-start;
    pointer-events: none;
  }
  .debug-health .player {
    display: flex; flex-direction: column; align-items: center;
    gap: 4px; min-width: 220px;
  }
  .debug-health .name { font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; opacity: 0.85; }
  .debug-health .name.you { color: #ffd56b; }
  .debug-health .name.ai  { color: #7ec4ff; }
  .debug-health .bar-label {
    font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase;
    opacity: 0.55; margin-top: 3px;
    min-width: 0; text-align: center;
    -webkit-text-stroke: 0; color: inherit;
  }
  .debug-health .bar {
    width: 100%; height: 10px;
    background: rgba(0,0,0,0.45); border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 0 0 1px rgba(255,255,255,0.06) inset;
  }
  .debug-health .bar > div {
    height: 100%; width: 100%;
    transition: width 120ms ease;
    transform-origin: left;
  }
  .debug-health .hp > div { background: linear-gradient(90deg, #6cf09e, #ffd05a 60%, #ff5a5a); }
  .debug-health .ko > div { background: linear-gradient(90deg, #c38bff, #6ce6ff); }
</style>
`;
